// @flow
import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {Col, Skeleton, Row, Descriptions} from "antd";
import {useTranslation} from "react-i18next";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {Img, IconCol, SelectField} from "./courierDedicationStyles";

import {formatPhoneNumber} from "../../../utils/helpers";
import placeholder from "../../../public/images/company-placeholder.jpg";
import {CourierIcon} from "../../_defaultComponents/CourierIcon/CourierIcon";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {toTurkishProperCase} from "utils/toTurkishProperCase";

export const CourierDedication = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["courierDedication"]);
    const [companyCourierList, setCompanyCourierList] = useState(true);
    const [searchText, setSearchText] = useState("");

    const [companyLoading, companyData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false
        })
    });

    const [dedicatedCouriersLoading, dedicatedCouriers] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getCouriersOfCompany.bind(null, {
            companyId
        })
    });

    const [poolCouriersLoading, poolCouriers] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getPoolCouriers.bind(null, {searchText}),
        dependencies: [searchText]
    });

    useEffect(() => {
        if (companyData.status && companyData.status !== 200) {
            stores.appStore.openErrorModal({
                content: t("other.companyNotFound", {ns: "common"}),
                onOk: () => {
                    history.push("/companies");
                }
            });
        }
    }, [companyData]);

    useEffect(() => {
        const couriers = dedicatedCouriers.map(courier => courier._id);
        setCompanyCourierList(couriers);
    }, [dedicatedCouriers]);

    return useObserver(() =>
        !companyData.data ? (
            <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
        ) : (
            <Row gutter={[0, 20]} data-testid="courierDedicationScreen">
                <Col span={24} data-testid="companyInformationCard">
                    <Descriptions
                        bordered
                        column={2}
                        title={
                            <>
                                <Row type="flex" justify="space-between" data-testid="companyName">
                                    {companyData.data.companyName}
                                </Row>
                                <Row>
                                    <Img
                                        src={companyData.data.photo || placeholder}
                                        data-testid="companyImage"
                                    />
                                </Row>
                            </>
                        }
                    >
                        <Descriptions.Item label={t("fields.city", {ns: "common"})}>
                            <span data-testid="city">
                                {toTurkishProperCase(companyData?.data?.city) || "-"}
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("fields.county", {ns: "common"})}>
                            <span data-testid="county">
                                {toTurkishProperCase(companyData?.data?.county) || "-"}
                            </span>
                        </Descriptions.Item>
                    </Descriptions>
                </Col>
                <Col span={24} data-testid="companyCourierCard">
                    <h4>{t("courierDedication.assignedCouriers", {ns: "courierDedication"})}</h4>
                    <SelectField
                        style={{width: "100%"}}
                        value={companyCourierList}
                        data-testid="companyCourierSelect"
                        onChange={value => {
                            setCompanyCourierList(value);
                        }}
                        searchValue={searchText}
                        showSearch
                        onSearch={value => {
                            setSearchText(value);
                        }}
                        mode="multiple"
                        filterOption={(inputValue, option) => {
                            return option.key.includes(inputValue);
                        }}
                        disabled={!stores.meStore.checkRole("assignCourier")}
                    >
                        {[...dedicatedCouriers, ...poolCouriers].map(courier => {
                            return (
                                <SelectField.Option
                                    key={courier.fullname}
                                    value={courier._id}
                                    data-testid="companyCourierSelect_option"
                                >
                                    <Row>
                                        <IconCol>
                                            <CourierIcon courierType={courier.type} />
                                        </IconCol>
                                        <Col>
                                            <Row>{courier.fullname}</Row>
                                            <Row>{formatPhoneNumber(courier.cellPhone)}</Row>
                                        </Col>
                                    </Row>
                                </SelectField.Option>
                            );
                        })}
                    </SelectField>
                </Col>
                {stores.meStore.checkRole("assignCourier") && (
                    <Col span={24}>
                        <Row justify="end">
                            <CustomButton
                                type="primary"
                                data-testid="submitButton"
                                onClick={async () => {
                                    stores.appStore.openConfirmModal({
                                        title: t("modal.confirmTitle", {ns: "common"}),
                                        content: t("modal.confirmChangeDedicatedCouriers", {
                                            ns: "courierDedication"
                                        }),
                                        okButtonProps: {"data-testid": "sureButton"},
                                        onOk: async () => {
                                            await stores.courierStore.dedicateCompanyCouriers({
                                                companyId,
                                                courierIdList: companyCourierList
                                            });
                                        }
                                    });
                                }}
                            >
                                {t("actions.save", {ns: "common"})}
                            </CustomButton>
                        </Row>
                    </Col>
                )}
            </Row>
        )
    );
};
